import React from 'react';
import sanitizeHtml from 'sanitize-html';
import dayjs from 'dayjs';
import globalStyles from '../../../../styles/exported.module.scss';
import styles from './CourseDetailsView.module.scss';
import { CourseDetailsViewProps } from './CourseDetails.types';
import PureHeading from '../../../common/Heading/PureHeading';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { courseDetailsIds } from '../../../../lib/tests/testIds';
import PureRichText from '../../../common/PureRichText/PureRichText';
import TrainingLink, { getCustomLinkElement } from '../../base/LearningLink/LearningLink';
import PureLink, { PureLinkVariant } from '../../../common/Link/PureLink';
import { CY_TRAINING_DETAILS } from '../../../../lib/constants/cypress';
import { Label } from '../../../common/Label/Label';
import { useScorm } from '../../../../lib/context/ScormContext';
import { COURSE_CARDS_LABEL_KEYS, COURSE_DETAILS_LABELS, SCORM_LABELS } from '../../../../lib/constants/labelKeys';
import { useConfig } from '../../../../lib/hooks/config';
import { DATE_FORMAT_FALLBACK } from '../../../../lib/constants/globals';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import useFeature from '../../../../lib/hooks/useFeature';
import { ALLOWED_RICHTEXT_TAGS } from '../../../../lib/constants/courses';
import { useViewContext } from '../../../../lib/context/ViewContext';
import Button from '../../../common/Button/Button';
import { Role, useRole } from '../../../../lib/context/SessionContext';
import ContentFileAdminActions from '../../base/ContentFileAdminActions/ContentFileAdminActions';
import { FeatureToggleCodename } from '../../../../lib/types/enums';
import { Api } from '../../../../lib/types/apiSpec';
import { DetailsLinkDestination } from '../../../../lib/hooks/useGetDetailsLink';
import LearningDetailsOrnaments from '../../../common/LearningDetailsOrnaments/LearningDetailsOrnaments';
import AssociateAssetDialog from '../../base/AssociateAssetDialog/AssociateAssetDialog';
import { groupByProperty } from '../../../../lib/utils/functional';
import { DownloadIcon } from '../../../../lib/icons/DowloadIcon';
import { distinctValuesByProp } from '../../../../lib/utils/distinctValues';

const CourseDetailsView = ({
  trainingNotAvailableMessage,
  originalId,
  elementsLabel,
  descriptionLabel,
  description,
  listElements,
  courseId,
  trainingName,
  type,
  connectFileSuccessMessage,
  disconnectFileSuccessMessage,
  isValidating,
  nameLabel,
  associatedAssets,
  associatedAssetsLabel,
  extraAssociatedAssets,
  ...rest
}: CourseDetailsViewProps) => {
  const { globalDateFormat = DATE_FORMAT_FALLBACK } = useConfig() ?? {};
  const { page } = useViewContext();
  const role = useRole();
  const { downloadsList, trainingDownloadsHistory, actionsState, addItem, removeItem } = useScorm();
  const scormFeatureEnabled = useFeature(FeatureToggleCodename.scormDownloadsList);
  const descriptionId = `course-details-${courseId}`;
  const elementsId = `elements-${courseId}`;
  const nameId = `name-${courseId}`;
  const associatedAssetsId = `associated-assets-${courseId}`;
  const downloadsListElement = downloadsList?.data?.find(el => el.trainingId === courseId);
  const scormLastDownload = trainingDownloadsHistory?.data?.[0];
  const downloadListLabel = downloadsListElement ? SCORM_LABELS.REMOVE : SCORM_LABELS.ADD;
  const scormActionsAvailable =
    scormFeatureEnabled &&
    role === Role.LEARNER_MANAGER &&
    (page.trainingActions?.canDownload || page.trainingActions?.canConnectContent);
  const isContentAdmin = !!page.trainingActions?.canConnectContent;

  const displayableAssociatedAssets = Object.entries(
    groupByProperty([...associatedAssets, ...(extraAssociatedAssets ?? [])], 'curriculumType'),
  );

  const handleDownloadClick = () => {
    if (downloadsListElement) {
      removeItem({
        id: downloadsListElement.id,
        trainingName: downloadsListElement.name,
        trainingType: downloadsListElement.type,
      });
    } else {
      addItem({ id: courseId, trainingName, trainingType: type });
    }
  };

  return (
    <div className={globalStyles.fullTabsWidth} data-testid={courseDetailsIds.courseDetailsContainer}>
      <div className={styles.stickyWrapper}>
        <div className={styles.stickyContainer}>
          <div className={styles.stickyColumn}>
            <TrainingLink
              className={styles.previewLink}
              id={courseId ?? ''}
              originalId={originalId}
              type={type}
              name={trainingName}
              ContentElement={getCustomLinkElement()}
              classNames={{ loader: styles.loaderContainer, notAvailableMessage: styles.notAvailableMessage }}
              notAvailableMessage={trainingNotAvailableMessage || ''}
              checkIfAvailable
              data-cy={CY_TRAINING_DETAILS.BUTTON}
              to={role === Role.GUEST ? DetailsLinkDestination.REGISTER : DetailsLinkDestination.LITMOS}
            />
            {scormActionsAvailable && (
              <>
                {page.trainingActions?.canDownload && (
                  <div className={styles.scormButtonContainer}>
                    <Button
                      variant="outlined"
                      disabled={actionsState[courseId]}
                      onClick={handleDownloadClick}
                      className={styles.scormDownloadButton}
                    >
                      <Label name={downloadListLabel} />
                    </Button>
                    {actionsState[courseId] && <RefreshLoader containerClass={styles.scormActionLoaderContainer} />}
                  </div>
                )}
                {trainingDownloadsHistory && scormLastDownload ? (
                  <div
                    className={styles.lastDownloadedTextContainer}
                    data-testid={courseDetailsIds.scormLastDownloaded}
                  >
                    <Label name={COURSE_DETAILS_LABELS.SCORM_LAST_DOWNLOADED} />
                    <time dateTime={scormLastDownload?.downloadDate}>
                      {dayjs(scormLastDownload?.downloadDate).format(globalDateFormat)}
                    </time>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
      <div className={styles.sectionBackgroundWrapper}>
        {nameLabel && (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionGrid}>
              <PureHeading level={HeadingLevel.h2} as={HeadingLevel.h3} className={styles.leftSubsection} id={nameId}>
                {nameLabel}
              </PureHeading>
              <p className={styles.description} aria-labelledby={nameId}>
                {trainingName}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className={styles.sectionBackgroundWrapper}>
        <div className={styles.sectionWrapper}>
          <div className={styles.sectionGrid}>
            <PureHeading
              level={HeadingLevel.h2}
              as={HeadingLevel.h3}
              className={styles.leftSubsection}
              id={descriptionId}
            >
              {descriptionLabel}
            </PureHeading>
            <p
              className={styles.description}
              aria-labelledby={descriptionId}
              data-testid={courseDetailsIds.courseDescription}
            >
              {!!description && (
                <PureRichText
                  richtext={sanitizeHtml(description, {
                    allowedTags: ALLOWED_RICHTEXT_TAGS,
                    allowedAttributes: false,
                  })}
                />
              )}
            </p>
          </div>
        </div>
      </div>
      <div className={styles.sectionBackgroundWrapper}>
        <div className={styles.sectionWrapper}>
          <div className={styles.sectionGrid}>
            <PureHeading level={HeadingLevel.h2} as={HeadingLevel.h3} className={styles.leftSubsection} id={elementsId}>
              {elementsLabel}
            </PureHeading>
            <section className={styles.elements} aria-labelledby={elementsId}>
              {isValidating && <RefreshLoader containerClass={styles.modulesRefreshLoader} />}
              {listElements?.map(({ name, description: elementDescription, id, contentFileId, contentFileUrl }) => (
                <dl id={id} key={id} className={styles.module}>
                  <dt className={styles.elementTitle}>
                    {name}
                    {type === Api.TrainingType.Course && page.trainingActions?.canConnectContent && (
                      <ContentFileAdminActions
                        id={id}
                        contentFileUrl={contentFileUrl}
                        contentFileId={contentFileId}
                        connectFileSuccessMessage={connectFileSuccessMessage}
                        disconnectFileSuccessMessage={disconnectFileSuccessMessage}
                      />
                    )}
                  </dt>
                  <dd className={styles.elementDescription}>
                    <p className={styles.elementDescriptionContent}>
                      {!!elementDescription && (
                        <PureRichText
                          richtext={sanitizeHtml(elementDescription, {
                            allowedTags: ALLOWED_RICHTEXT_TAGS,
                            allowedAttributes: false,
                          })}
                        />
                      )}
                    </p>
                    {type === Api.TrainingType.LearningPath && (
                      <TrainingLink
                        originalId={originalId}
                        id={id}
                        type={Api.TrainingType.Course}
                        name={name ?? ''}
                        variant={PureLinkVariant.Link}
                        className={styles.elementLink}
                        card
                      >
                        <Label
                          name={
                            role === Role.GUEST
                              ? COURSE_CARDS_LABEL_KEYS.LINK.PREVIEW_COURSE
                              : COURSE_CARDS_LABEL_KEYS.LINK.COURSE_DETAILS
                          }
                        />
                      </TrainingLink>
                    )}
                  </dd>
                </dl>
              ))}
            </section>
          </div>
        </div>
        <LearningDetailsOrnaments stethoscopeClassName={styles.stethoscope} heartClassName={styles.heart} />
      </div>
      {(isContentAdmin || !![...associatedAssets, ...(extraAssociatedAssets ?? [])].length) && (
        <div className={styles.sectionBackgroundWrapper} data-testid={courseDetailsIds.associatedAssets}>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionGrid}>
              <PureHeading
                level={HeadingLevel.h2}
                as={HeadingLevel.h3}
                className={styles.leftSubsection}
                id={associatedAssetsId}
              >
                {associatedAssetsLabel}
              </PureHeading>
              <section className={styles.elements} aria-labelledby={associatedAssetsId}>
                {isValidating && <RefreshLoader containerClass={styles.modulesRefreshLoader} />}
                {isContentAdmin && <AssociateAssetDialog type={type} selected={associatedAssets} {...rest} />}
                {displayableAssociatedAssets.map(([curriculumType, assets]) => (
                  <div
                    key={curriculumType}
                    role="group"
                    className={styles.associatedAssetGroup}
                    aria-labelledby={`${curriculumType}-title`}
                  >
                    <PureHeading
                      level={HeadingLevel.h4}
                      className={styles.associatedAssetsTitle}
                      id={`${curriculumType}-title`}
                    >
                      {curriculumType}
                    </PureHeading>
                    <ul className={styles.associatedAssetsList}>
                      {distinctValuesByProp(assets, 'id').map(asset => (
                        <li key={asset.id}>
                          <PureLink
                            href={asset.assetReference}
                            className={styles.associatedAssetLink}
                            tabBehaviour="forceNewTab"
                          >
                            <DownloadIcon aria-hidden="true" />
                            {asset.name}
                          </PureLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            </div>
          </div>
          <LearningDetailsOrnaments stethoscopeClassName={styles.stethoscope} heartClassName={styles.heart} />
        </div>
      )}
    </div>
  );
};
export default CourseDetailsView;
